import React, { useState, useEffect } from "react";
import { Heading, Box } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import IssbContainer from "../IssbContainer";
import AIReviewSourcesPanel from "./AIReviewSourcesPanel";
import AIReviewQuestionsPanel from "./AIReviewQuestionsPanel";
import AIReviewRelaxPanel from "./AIReviewRelaxPanel";
import WizardStepWrapper from "../WizardStepWrapper";
import { postQuestionsAnswerJob } from "api/ai/ai.service";
import { normalizeFileInfoSpec } from "utils/fileinfoIds";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import questionnaireService from "../../../api/issb/questionnaire.service";

const STEPS = ["SELECT_SOURCES", "SELECT_REQUIREMENTS", "RELAX"];

export default function AIReviewPage() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [sourceIds, setSourceIds] = useState([]);
  const [questionIds, setQuestionIds] = useState([]);
  const [questionFilter, setQuestionFilter] = useState("");
  const [sourceRoundIds, setSourceRoundIds] = useState([]);
  const [roundQuestionIds, setRoundQuestionIds] = useState([]);
  // Default ON: an org that has approved figures almost always wants them used, and the years themselves are
  // chosen by the selector below. (v1's question-id source is hidden since ESG-4024 and no longer sent.)
  const [foundationV2Enabled, setFoundationV2Enabled] = useState(true);
  const [foundationV2Years, setFoundationV2Years] = useState([]);
  const [documentsEnabled, setDocumentsEnabled] = useState(false);
  const [otherRoundsEnabled, setOtherRoundsEnabled] = useState(false);
  const [task, setTask] = useState(null);
  const roundSelector = useSelector((state) => state.issbRound);
  const questionnaireId = roundSelector.selectedRound.id;

  const queryClient = useQueryClient();

  const { data: questionnaireData } = useQuery(questionnaireId, () => questionnaireService.get(questionnaireId), {
    enabled: !!questionnaireId,
  });
  // Wizard defaults to no rounds selected — user must explicitly choose
  useEffect(() => {
    setSourceRoundIds([]);
  }, [questionnaireData]);

  const gotoNext = () => {
    // kick off the review?
    if (stepIndex === 1) {
      const allQuestionIds = otherRoundsEnabled && roundQuestionIds.length > 0 ? roundQuestionIds : [];
      const fileinfoSpec = normalizeFileInfoSpec({
        other_fileinfo_ids: documentsEnabled ? sourceIds : [],
        round_ids: otherRoundsEnabled ? sourceRoundIds : [],
        question_ids: allQuestionIds.length > 0 ? allQuestionIds : undefined,
        /*
          Foundation Data's reporting years. Kept out of `question_ids` — where v1's selection used to go —
          because these are not ids of anything indexed: the AI service fetches each year's approved figures
          whole and injects them as their own prompt block.

          Note the trap this shares with the fields above: normalizeFileInfoSpec turns an empty array into
          `undefined`, and the AI service reads a MISSING `other_fileinfo_ids` as "use every file in the round
          folder" — which is why documents-off still sends `[]` there, and why turning Document Sources off
          does less than it looks like. Not this change's to fix; just do not make it worse.
        */
        foundation_year_ids:
          foundationV2Enabled && foundationV2Years.length > 0 ? foundationV2Years : undefined,
      });

      postQuestionsAnswerJob({
        questionnaireId,
        questionIds,
        filterLabel: questionFilter || undefined,
        overwrite: "AI",
        draft: true,
        fileinfoSpec,
      }).then((task) => {
        setTask(task);
        queryClient.invalidateQueries("taskService.list");
      });
    }
    setStepIndex(stepIndex + 1);
  };

  return (
    <IssbContainer>
      <Box mr="300px">
        <WizardStepWrapper
          nextText={stepIndex === 1 ? "Start Review" : "Next"}
          previousText="Previous"
          gotoNext={gotoNext}
          gotoPrevious={stepIndex === 2 ? false : () => setStepIndex(stepIndex - 1)}
          onCancel={() => navigate(-1)}
          stepIndex={stepIndex}
          steps={STEPS}
          superTitle="AI Review"
          nextDisabled={nextDisabled}
        >
          {/* TODO: local tweat, to remove back */}
          {stepIndex === 0 && (
            <AIReviewSourcesPanel
              setNextDisabled={setNextDisabled}
              sourceIds={sourceIds}
              setSourceIds={setSourceIds}
              sourceRoundIds={sourceRoundIds}
              setSourceRoundIds={setSourceRoundIds}
              foundationV2Enabled={foundationV2Enabled}
              setFoundationV2Enabled={setFoundationV2Enabled}
              foundationV2Years={foundationV2Years}
              setFoundationV2Years={setFoundationV2Years}
              documentsEnabled={documentsEnabled}
              setDocumentsEnabled={setDocumentsEnabled}
              otherRoundsEnabled={otherRoundsEnabled}
              setOtherRoundsEnabled={setOtherRoundsEnabled}
              roundQuestionIds={roundQuestionIds}
              setRoundQuestionIds={setRoundQuestionIds}
            />
          )}
          {stepIndex === 1 && (
            <AIReviewQuestionsPanel
              questionIds={questionIds}
              setQuestionIds={setQuestionIds}
              questionFilter={questionFilter}
              setQuestionFilter={setQuestionFilter}
            />
          )}
          {stepIndex === 2 && <AIReviewRelaxPanel task={task} />}
        </WizardStepWrapper>
      </Box>
    </IssbContainer>
  );
}
